import { Author } from "./author";
import {
  asBoolean,
  asDate,
  asNumber,
  asString,
  isObject,
} from "./model-utils";
import {
  enumValue,
  ProductNode,
  ProductStatus,
  WeightType,
  WeightUnit,
} from "./enums";

export class Product {
  [key: string]: any;
  constructor(json: any = {}) {
    Object.assign(this, json);
    this.productId = asString(json.productId);
    this.profileId = asString(json.profileId);
    this.productCategoryId = asString(json.productCategoryId);
    this.productCategoryName = asString(json.productCategoryName);
    this.name = asString(json.name);
    this.description = asString(json.description);
    this.amount = asNumber(json.amount);
    this.cost = asNumber(json.cost);
    this.sellingPrice = asNumber(json.sellingPrice);
    this.discountPrice = asNumber(json.discountPrice);
    this.promoPrice = asNumber(json.promoPrice);
    this.markUpPrice = asNumber(json.markUpPrice);
    this.sku = asNumber(json.sku);
    this.quantity = asNumber(json.quantity) ?? 0;
    this.currency = asString(json.currency);
    this.status = enumValue(ProductStatus, json.status);
    this.node = enumValue(ProductNode, json.node);
    this.parentId = asString(json.parentId);
    this.accessPointId = asString(json.accessPointId);
    this.accessPointName = asString(json.accessPointName);
    this.onlineStore = asBoolean(json.onlineStore);
    this.unlimited = asBoolean(json.unlimited);
    this.activated = asBoolean(json.activated);
    this.displayPrice = asBoolean(json.displayPrice);
    this.itemEdited = asBoolean(json.itemEdited);
    this.requestedUpdate = asBoolean(json.requestedUpdate);
    this.reviewRequired = asBoolean(json.reviewRequired);
    this.reviewReason = asString(json.reviewReason);
    this.isVariant = asBoolean(json.isVariant);
    this.vatExclude = asBoolean(json.vatExclude);
    this.inventoryScopedProducts = asBoolean(json.inventoryScopedProducts);
    this.logisticsScopedProducts = asBoolean(json.logisticsScopedProducts);
    this.warningThreshold = asNumber(json.warningThreshold);
    this.thresholdNotification = asBoolean(json.thresholdNotification);
    this.expire = asBoolean(json.expire);
    this.expiryDate = asDate(json.expiryDate);
    this.productSectionName = asString(json.productSectionName);
    this.productSectionId = asString(json.productSectionId);
    this.productSubCategoryName = asString(json.productSubCategoryName);
    this.productSubCategoryId = asString(json.productSubCategoryId);
    this.manualCompute = asBoolean(json.manualCompute);
    this.useDiscount = asBoolean(json.useDiscount);
    this.usePromo = asBoolean(json.usePromo);
    this.useMarkUp = asBoolean(json.useMarkUp);
    this.itemIdentifier = asString(json.itemIdentifier);
    this.itemDescription = asString(json.itemDescription);
    this.jobNo = asString(json.jobNo);
    this.pickUpAddress = asString(json.pickUpAddress);
    this.pickupCountry = asString(json.pickupCountry);
    this.pickUpCountryCode = asString(json.pickUpCountryCode);
    this.deliveryAddress = asString(json.deliveryAddress);
    this.deliveryCountry = asString(json.deliveryCountry);
    this.deliveryCountryCode = asString(json.deliveryCountryCode);
    this.referenceSku = asNumber(json.referenceSku);
    this.referenceSkuDate = asDate(json.referenceSkuDate);
    this.printCount = asNumber(json.printCount);
    this.isPackQuantity = asBoolean(json.isPackQuantity);
    this.packQuantity = asNumber(json.packQuantity);
    this.requestedUnit = asNumber(json.requestedUnit);
    this.matchScore = asNumber(json.matchScore);
    this.searchTags = Array.isArray(json.searchTags)
      ? json.searchTags.map(String)
      : undefined;
    this.weight = asNumber(json.weight);
    this.weightCategoryId = asString(json.weightCategoryId);
    this.weightCategoryName = asString(json.weightCategoryName);
    this.weightUnit = enumValue(WeightUnit, json.weightUnit);
    this.weightType = enumValue(WeightType, json.weightType);
    this.media = Array.isArray(json.media) ? json.media : [];
    this.supportingDocuments = Array.isArray(json.supportingDocuments)
      ? json.supportingDocuments
      : undefined;
    this.moq = Array.isArray(json.moq) ? json.moq : undefined;
    this.batch = Array.isArray(json.batch) ? json.batch : undefined;
    this.variants = Array.isArray(json.variants)
      ? json.variants.map(Product.fromJson)
      : undefined;
    this.variant = isObject(json.variant) ? { ...json.variant } : json.variant;
    this.author = Author.fromJson(json.author);
    this.publishBy = Author.fromJson(json.publishBy);
  }

  static fromJson(json: any) {
    return isObject(json) ? new Product(json) : json;
  }
}
